import * as call from "./api.impl";
import {CONTEXT_ID_HEADER, RequestBuilder} from "./api.impl";
import {IF_MATCH_HEADER} from "utils/constants";
import {fetchEventSource} from "@microsoft/fetch-event-source";
import {launchDarkly} from "utils/launchDarkly";
import {encodeToBase64UTF8} from "utils/valueFormatter.function.js";
import {isConnectLogin} from "utils/toggleConnectOdp.js";
import {memoize} from "lodash";

/**
 * Returns the root URL for the API based on the current environment and login method.
 * Does not end with /
 *  that hassle with existence of variable is because of unit tests
 * @type {(function(): string) & MemoizedFunction}
 */
export const getApiRootUrl = memoize(internal_getApiRootUrl);

function internal_getApiRootUrl() {
    let rootUrl = "";
    if (typeof window.ENVIRONMENT === "undefined") {
        rootUrl = "";
    } else if (isConnectLogin()) {
        rootUrl = window.ENVIRONMENT.APPROVAL_API_OAUTH;
    } else {
        rootUrl = window.ENVIRONMENT.APPROVAL_API;
    }
    return rootUrl + "/jaxrs";
}

const IGNORE_VISMA_CONTEXTID = true;

function getUseNewGLAsApiFlag() {
    return launchDarkly.userClient?.variation("new-glas-api", false);
}

function getUseNewMyHistoryApiFlag() {
    return launchDarkly.userClient?.variation("new-my-history-api", false);
}

function getUseNewNearestManagerApiFlag() {
    return launchDarkly.userClient?.variation("new-nearest-manager-api", false);
}

function getUseNewTokenApiFlag() {
    return launchDarkly.userClient?.variation("new-token-api", false);
}

function getUseNewAmountLimitApiFlag() {
    return launchDarkly.userClient?.variation("new-amount-limit-api", false);
}

export function getUserData() {
    return new RequestBuilder(getApiRootUrl())
        .path("startup")
        .get();
}

export function getUserContexts() {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("me")
        .path("tenants")
        .get();
}

export function getAppMenuItems() {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("me")
        .path("menu-items")
        .get();
}

export function getWorkflowGraphData(taskKey) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("processes")
        .path(taskKey)
        .path("progress-details")
        .get();
}

export function getWorkflowGraphDataForDocumentOverview(documentKey, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("documents")
        .path(documentKey)
        .path("progress-details")
        .get();
}

export function getProcessByUID(uid) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("processes")
        .path(uid)
        .get();
}

export function changeUserContext(contextId) {
    return call.get(getApiRootUrl() + "/startup?contextSelector:switchContext=" + contextId);
}

export function userSearch(prefix, companyId, taskId = undefined, excludeSysAdmins = true) {
    const data = {
        prefix: prefix,
        excludeSysAdmins: excludeSysAdmins,
    };
    if (taskId) {
        if (Array.isArray(taskId)) {
            data["selected_tasks"] = JSON.stringify(taskId);
        } else {
            data["task_key"] = taskId;
        }
    }
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("users")
        .path("search")
        .formData(data)
        .post();
}

export function singleTaskUserSearch(data, taskId) {
    if (taskId)
        data["task_key"] = taskId;
    return call.postForm(getApiRootUrl() + "/UserBean/userSearch", data);
}

export function multipleTasksUserSearch(data, taskId) {
    if (taskId)
        data["selected_tasks"] = JSON.stringify(taskId);
    return call.postForm(getApiRootUrl() + "/UserBean/userSearch", data);
}

export function getCompanyUser(userId) {
    return new Promise((resolve, reject) => {
        if (userId == null) {
            reject("userId cannot be null");
            return;
        }
        call.get(getApiRootUrl() + "/UserBean/companyuser/" + userId).then(response => {
            // response.idUser != null is necessary because response === true when the http status code is 204
            if (response != null && response.idUser != null) {
                resolve(response);
            } else {
                resolve({
                    valid: false,
                    idUser: userId,
                    fullNameOrEmail: null,
                });
            }
        }, error => {
            reject(error);
        });
    });
}

export function getWsToken() {
    if (getUseNewTokenApiFlag()) {
        return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
            .path("me")
            .path("token")
            .path("websocket")
            .get();
    }

    return call.get(getApiRootUrl() + "/token/websocket");
}

export function getTokenWithScope(scopes) {

    const data = {
        scopes: scopes,
    }

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("me")
        .path("token")
        .path("exchange")
        .jsonData(data)
        .post();

}


export function getAvailableColumnsForDocType(docType) {
    return call.get(getApiRootUrl() + "/CompanyMetadataBean/availableColumnsForDocumentType/" + docType);
}

/**
 * backend doesn't expose elementary function for each of user settings, so we have to load all, modify and save all (see application.reducer#getUserSettings)
 * see user.action.js#hideGuidedTour for example of usage
 * @param userSettings
 */
export function updateUserSettings(userSettings = {}, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("me")
        .path("user-settings")
        .jsonData(userSettings)
        .post();
}

export function getUserSettings(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("me")
        .path("user-settings")
        .get();
}

export function getWorkflowsAmountLimits(companyId) {
    if (getUseNewAmountLimitApiFlag()) {
        return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
            .path("tenant")
            .path(companyId)
            .path("amount-limits")
            .get();
    }
    return call.get(getApiRootUrl() + "/AmountLimitBean/list");
}

export function getAmountLimitDetails(id, companyId) {
    if (getUseNewAmountLimitApiFlag()) {
        return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
            .path("tenant")
            .path(companyId)
            .path("amount-limits")
            .path(id)
            .get();
    }
    return call.get(getApiRootUrl() + "/AmountLimitBean/getAmountLimitDetails/" + id);
}

export function deleteAmountLimit(id, confirm, companyId) {
    if (getUseNewAmountLimitApiFlag()) {
        return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
            .path("tenant")
            .path(companyId)
            .path("amount-limits")
            .path(id)
            .path(confirm ? "confirm" : "")
            .delete();
    }

    let data = {
        limitId: id,
        confirm: confirm,
    };
    return call.postForm(getApiRootUrl() + "/AmountLimitBean/delete", data);
}

export function deleteCurrency(id, currency) {
    let data = {
        limitId: id,
        currency: currency,
    };
    return call.postForm(getApiRootUrl() + "/AmountLimitBean/deleteCurrency", data);
}

export function editAmountLimit(id, name, companyId) {
    let data = {
        limitId: id,
        name: name,
    };

    if (getUseNewAmountLimitApiFlag()) {
        return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
            .path("tenant")
            .path(companyId)
            .path("amount-limits")
            .path(id)
            .formData(data)
            .put();
    }
    return call.postForm(getApiRootUrl() + "/AmountLimitBean/editAmountLimit", data);
}

export function addAmountLimit(name, companyId) {
    let data = {
        name: name,
        id: 0,
        amounts: {},
    };

    if (getUseNewAmountLimitApiFlag()) {
        return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
            .path("tenant")
            .path(companyId)
            .path("amount-limits")
            .jsonData(data)
            .post();
    }
    return call.postForm(getApiRootUrl() + "/AmountLimitBean/addAmountLimit", data);
}

export function addCurrencyAmountLimit(id, currency, amount) {
    let data = {
        limitId: id,
        currency: currency,
        amount: amount,
    };
    return call.postForm(getApiRootUrl() + "/AmountLimitBean/addCurrencyAmountLimit", data);
}

export function getAllCurrencies() {
    return call.get(getApiRootUrl() + "/RuleConfigurationBean/getAllCurrencies");
}

//region Accounting

export function getCostUnitTypes() {
    return call.get(getApiRootUrl() + "/cost-unit-types", null, true);
}

export function setCostUnitTypes(data, version) {
    let header = {};
    header[IF_MATCH_HEADER] = version;
    return call.postForm(getApiRootUrl() + "/RuleConfigurationBean/setCostUnitsTypesOrder", data, header);
}

export function lookupAccount(data, dimension, companyId) {
    if (getUseNewGLAsApiFlag()) {
        return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
            .path("tenant")
            .path(companyId)
            .path("glas")
            .queryParam("prefix", data.prefix)
            .queryParam("page", data.page)
            .queryParam("rows", data.rows)
            .queryParam("usedInIndexOnly", data.usedInIndexOnly)
            .queryParam("showInactive", data.showInactive)
            .get();
    }
    return call.get(getApiRootUrl() + "/glas" + call.objectToQueryString(data));
}

export function lookupAllAccounts(dataArray, data) {
    const [taskId, companyId] = dataArray;

    if (getUseNewGLAsApiFlag()) {
        return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
            .path("tenant")
            .path(companyId)
            .path("glas")
            .queryParam("page", data.page)
            .queryParam("rows", data.rows)
            .queryParam("usedInIndexOnly", data.usedInIndexOnly)
            .queryParam("showInactive", data.showInactive)
            .get();
    }
    return call.get(getApiRootUrl() + "/AccountingGrid/" + taskId + "/glas" + call.objectToQueryString(data));
}

export function lookupAllAccountsWithAccountNumber(taskId, accountNumber) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("accounting-grid")
        .path(taskId)
        .path("glas")
        .path(accountNumber)
        .get();
}

export function _lookupAllCostCenters(ids, data) {
    let [taskId, index] = ids;
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("accounting-grid")
        .path(taskId)
        .path("columns")
        .path(index)
        .path("cost-units")
        .queryParams(data)
        .get();
}

export function _lookupAllCostCentersWithAccountNumber(ids, accountNumber) {
    let [taskId, index] = ids;
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("accounting-grid")
        .path(taskId)
        .path("columns")
        .path(index)
        .path("cost-units")
        .path(accountNumber)
        .get();
}

export function lookupCostCenters(data, dimension) {
    return call.get(getApiRootUrl() + "/cost-unit-types/" + dimension + "/cost-units" + call.objectToQueryString(data));
}

export function showHistorySearch() {
    return call.get(getApiRootUrl() + "/Accounting/showSearch");
}

export function _updateAccoutingCell(taskId, rowid, cellid, value, rowVersion) {
    let data = {
        columnId: cellid,
        value: value,
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("accounting-grid")
        .path(taskId)
        .path("rows")
        .path(rowid)
        .jsonData(data)
        .header(IF_MATCH_HEADER, rowVersion)
        .put();
}

export function getLineApprovalForRow(key, rowIndex) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("accounting-grid")
        .path(key)
        .path("lineapproval")
        .path(rowIndex)
        .get();
}

export function _accoutingLineAction(taskId, rowId, status, rowVersion) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("accounting-grid")
        .path(taskId)
        .path("lineapproval")
        .path(rowId)
        .path(status)
        .header(IF_MATCH_HEADER, rowVersion)
        .post();
}

export function _deleteAccoutingRow(taskId, rowIndex, rowVersion) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("accounting-grid")
        .path(taskId)
        .path("rows")
        .path(rowIndex)
        .header(IF_MATCH_HEADER, rowVersion)
        .delete();
}

export function addNewAccoutingRow(taskId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("accounting-grid")
        .path(taskId)
        .path("rows")
        .post();
}

export function findAccount(dataArray, value) {
    let [taskId, companyId] = dataArray;
    const data = {
        prefix: value,
    };

    if (getUseNewGLAsApiFlag()) {
        return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
            .path("tenant")
            .path(companyId)
            .path("glas-indexed")
            .queryParam("prefix", value)
            .get();
    }

    return call.get(getApiRootUrl() + "/AccountingGrid/" + taskId + "/glas-indexed" + call.objectToQueryString(data));
}

export function _findCostCenter(ids, value) {
    let [taskId, columnId] = ids;
    let data = {
        prefix: value,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("accounting-grid")
        .path(taskId)
        .path("columns")
        .path(columnId)
        .path("cost-units-indexed")
        .queryParams(data)
        .get();
}

export function _getAccountingGridRows(taskKey) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("accounting-grid")
        .path(taskKey)
        .path("rows")
        .get();
}

export function _getAccountingGridHeader(taskKey) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("accounting-grid")
        .path(taskKey)
        .get();
}

export function getLineApprovalByUID(uid) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("accounting-grid")
        .path(uid)
        .path("lineapproval")
        .get();
}

//endregion

//region CommentBean

export function getLastCommentByUID(uid) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(uid)
        .path("last-comment")
        .get();
}

export function insertComment(taskId, comment) {
    const data = {
        key: taskId,
        text: encodeURIComponent(comment),
    };
    return call.postForm(getApiRootUrl() + "/CommentBean/insertComment", data);
}

//endregion

//region new Comments endpoints

export function getTasksCommentsByUid(uid) {
    return call.get(getApiRootUrl() + "/tasks/" + uid + "/comments");
}

export function getProcessPermissions(uid) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("processes")
        .path(uid)
        .path("permissions")
        .get();
}

export function getDocumentPermissions(uid) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("documents")
        .path(uid)
        .path("permissions")
        .get();
}

export function insertDocumentsCommentByUID(uid, comment) {
    const data = {
        comment: comment,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("documents")
        .path(uid)
        .path("comments")
        .jsonData(data)
        .post();
}

export function insertProcessesCommentByUID(uid, comment) {
    const data = {
        comment: comment,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("processes")
        .path(uid)
        .path("comments")
        .jsonData(data)
        .post();
}

export function getProcessesComments(processKey) {
    return call.get(getApiRootUrl() + "/processes/" + processKey + "/comments");
}

export function getDocumentsComments(documentKey) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("documents")
        .path(documentKey)
        .path("comments")
        .get();
}

export function insertTasksComment(taskKey, comment) {
    const data = {
        comment: comment,
    };
    return call.postJson(getApiRootUrl() + "/tasks/" + taskKey + "/comments", data);
}

//endregion

//region new Draft Comments endpoints

export function insertDraftComment(documentUid, comment) {
    const data = {
        comment: comment,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("documents")
        .path(documentUid)
        .path("draft-comment")
        .jsonData(data)
        .post();
}


export function getDraftComment(documentUid) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("documents")
        .path(documentUid)
        .path("draft-comment")
        .get();
}

export function deleteDraftComment(documentUid) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("documents")
        .path(documentUid)
        .path("draft-comment")
        .delete();
}

//endregion

//region CompanyBean

export function getCompanyDocumentTypes(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("document-types")
        .get();
}

export function getCompanyDocumentTypesBasedOnFeatures(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("document-types-based-on-features")
        .get();
}

export function getDocumentTypesConfiguration(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("document-types-configurations")
        .get();
}

export function getDocumentTypesForWorkflowConfiguration(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("document-types-for-workflow-configuration")
        .get();
}

export function postDocumentTypesConfiguration(companyId, data) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("document-types-configurations")
        .jsonData(data)
        .post();
}

export function getCurrencyCode(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("default-currency-code")
        .get();
}

export function getEnabledFeaturesForDocumentType(documentType, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("document-types")
        .path(documentType)
        .path("enabled-features")
        .get();
}


//endregion

//region DocumentOverview

export function getDocumentOverview(tab, counter, data, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("documents")
        .path("search-by-status")
        .path(counter)
        .queryParam("documentType", tab.toString())
        .formData(data)
        .post();
}

export function getDocumentOverviewCounter(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("documents")
        .path("counters")
        .get();
}

export function getDocumentOverviewDetails(key, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("documents")
        .path(key)
        .path("document-details")
        .get();

}

export function approveDocumentDirectly(comment, selectedDocuments, companyId) {
    let data = {
        selected_documents: JSON.stringify(selectedDocuments),
        comment: comment,
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("documents")
        .path("approve-directly")
        .formData(data)
        .post();

}

export function rejectDocumentDirectly(comment, selectedDocuments, companyId) {
    let data = {
        selected_documents: JSON.stringify(selectedDocuments),
        comment: comment,
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("documents")
        .path("reject-directly")
        .formData(data)
        .post();

}

export function cancelDocument(companyId, documentKey, comment) {

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("documents")
        .path(documentKey)
        .path("cancel")
        .formData({comment: comment})
        .post();
}

export function sendDocumentForApproval(comment, selectedDocuments, companyId) {
    let data = {
        selected_documents: JSON.stringify(selectedDocuments),
        comment: comment,
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("documents")
        .path("send-for-approval")
        .formData(data)
        .post();

}

export function getDocumentOverviewEditor(taskKey, companyId) {

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("documents")
        .path(taskKey)
        .path("external-editor")
        .get();
}

//endregion

//region Departments

export function getDepartments(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("department-managers")
        .path("Departments")
        .get();
}

export function getAssignedDepartmentManagers(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("department-managers")
        .path("AssignedDepartmentManagers")
        .get();
}

export function getPossibleDepartmentManagers(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("department-managers")
        .path("PossibleDepartmentManagers")
        .get();
}

export function saveDepartmentManagers(data, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("department-managers")
        .path("DepartmentManagers")
        .jsonData(data)
        .post();
}


//endregion

//region NearestManagers

export function getNearestManagers(page, itemsPerPage, nearestManagerMode, asc, name, companyId) {

    if (companyId && getUseNewNearestManagerApiFlag()) {
        return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
            .path("tenant")
            .path(companyId)
            .path("nearest-managers")
            .queryParam("name", name)
            .queryParam("page", page)
            .queryParam("itemsPerPage", itemsPerPage)
            .queryParam("nearestManagerMode", nearestManagerMode)
            .queryParam("asc", asc)
            .get();
    }

    const data = {
        page: page,
        itemsPerPage: itemsPerPage,
        nearestManagerMode: nearestManagerMode,
        asc: asc,
        name: name,
    };
    return call.get(getApiRootUrl() + "/NearestManagers" + call.objectToQueryString(data));
}

//endregion


//region FileUpload

export function getHistoryAttachments(taskId) {
    return call.get(getApiRootUrl() + "/FileUpload/getAttachmentPageKeysNewUI/" + taskId);
}

export function getAttachmentsForProcess(processUid) {
    return call.get(getApiRootUrl() + "/processes/" + processUid + "/attachments");
}

export function deleteAttachment(taskId, collectionId, attachmentId) {
    return new RequestBuilder(getApiRootUrl())
        .path("FileUpload")
        .path("delete")
        .path(taskId)
        .path(collectionId)
        .path(attachmentId)
        .post();
}

export function addAttachment(taskId, title, description, file) {
    const data = new FormData();
    data.append("title", title);
    data.append("description", description);
    data.append("input_browse", file);

    return new RequestBuilder(getApiRootUrl())
        .path("FileUpload")
        .path("upload")
        .path(taskId)
        .multipartData(data)
        .post();
}

export function getDocumentOverviewAttachments(taskId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("documents")
        .path(taskId)
        .path("attachments")
        .get();
}


export function getTaskAttachments(uid, ids) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(uid)
        .path("original-attachments")
        .queryParam("attachmentIds", ids)
        .get();
}

export function getDocumentAttachments(uid, ids, documentVersion) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("documents")
        .path(uid)
        .path("original-attachments")
        .queryParam("attachmentIds", ids)
        .get();
}

export function getProcessAttachments(uid, ids) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("processes")
        .path(uid)
        .path("original-attachments")
        .queryParam("attachmentIds", ids)
        .get();
}

//endregion

//region HistoryBean

export function getHistoryTasks(data) {
    let {type} = data;

    if (getUseNewMyHistoryApiFlag()) {
        return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
            .path("my-history")
            .path(type)
            .formData(data)
            .post();
    }

    return call.postForm(getApiRootUrl() + "/HistoryBean/" + type, data);
}

export function getProcessStates() {
    return call.get(getApiRootUrl() + "/HistoryBean/getProcessStates/");
}

//endregion

//region MailBean

export function emailTask(taskId, to, comment, subject, withAttachment, withCopyToMe, cc, bcc) {
    const data = {
        key: taskId,
        to: to,
        message: comment,
        subject: subject,
        sendAttachment: withAttachment,
        sendCopy: withCopyToMe,
        cc: cc,
        bcc: bcc,
    };
    const formData = {
        mailreq: JSON.stringify(data),
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("processes")
        .path("send-by-email")
        .formData(formData)
        .post();
}

export function emailDocument(taskId, to, comment, subject, withAttachment, withCopyToMe, cc, bcc, companyId) {
    const data = {
        key: taskId,
        to: to,
        message: comment,
        subject: subject,
        sendAttachment: withAttachment,
        sendCopy: withCopyToMe,
        cc: cc,
        bcc: bcc,
    };
    const formData = {
        mailreq: JSON.stringify(data),
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("documents")
        .path("send-by-email")
        .formData(formData)
        .post();

}

export function getUserEmails(prefix) {
    const data = {
        prefix: prefix,
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("me")
        .path("address-book")
        .path("search")
        .formData(data)
        .post();
}

//endregion

//region MailSettingsBean

export function getEmailSettings() {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("me")
        .path("mail-settings")
        .get();
}

export function updateEmailSettings(data) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("me")
        .path("mail-settings")
        .jsonData(data)
        .post();
}

//endregion

//region OrganizationChart

export function getOrganizationChart(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("organization-chart")
        .path("listRoles")
        .get();
}

export function getOrganizationChartForDate(date, data, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("organization-chart")
        .path("getChildren")
        .path(date)
        .formData(data)
        .post();
}

export function getOrganizationChartRoles(roleId, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("organization-chart")
        .path("getRoles")
        .path(roleId)
        .get();
}

export function getOrganizationChartUsers(date, unitId, level, data, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("organization-chart")
        .path("getUsers")
        .path(unitId)
        .path(level)
        .path(date)
        .formData(data)
        .post();
}


//endregion

//region Payment Approval

export function getActiveBatches() {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("payment-approval")
        .path("batches")
        .path("active")
        .get();
}

export function createBatch() {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("payment-approval")
        .path("batches")
        .put();
}

export function addToBatch(batchId, version, taskId) {
    let data = {
        version: version,
        taskId: taskId,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("payment-approval")
        .path("batches")
        .path(batchId)
        .path("tasks")
        .jsonData(data)
        .put();
}

export function changeBatch(batchId, version, state, secret) {
    let data = {
        version: version,
        state: state,
        //secret: secret
    };
    if (secret)
        data.secret = secret;
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("payment-approval")
        .path("batches")
        .path(batchId)
        .jsonData(data)
        .post();
}

export function deleteBatch(batchId, taskId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("payment-approval")
        .path("batches")
        .path(batchId)
        .path("tasks")
        .path(taskId)
        .delete();
}

//endregion

//region ProjectManagerBean

export function getGeneralApprovers(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("project-managers")
        .path("GeneralApprovers")
        .get();
}

export function updateGeneralApprovers(data, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("project-managers")
        .path("GeneralApprover")
        .jsonData(data)
        .put();
}

export function deleteGeneralApprovers(id, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("project-managers")
        .path("GeneralApprover")
        .path(id)
        .delete();
}


//endregion

//region RoleChartBean

export function getRuleConfigurationRoles(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("roles")
        .get();
}

export function getRuleConfigurationRoleById(companyId, id) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("roles")
        .path(id)
        .get();
}

export function addRuleConfigurationRoles(companyId, data) {
    let postData = {
        name: data,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("roles")
        .jsonData(postData)
        .post();
}

export function updateRuleConfigurationRoles(companyId, data) {
    const id = data.idRole;
    let postData = {
        name: data.name,
        roleId: data.idRole,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("roles")
        .path(id)
        .jsonData(postData)
        .put();
}

export function deleteRuleConfigurationRoles(companyId, id, removeDependencies) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("roles")
        .path(id)
        .queryParam("removeDependencies", removeDependencies)
        .delete();
}

export function getCostUnitNodes(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .get();
}

/**
 * @typedef {Object} AddCostUnitNodeRequest
 * @property {Object.<number, string|null>} costUnits
 */
/**
 *
 * @param {string} companyId
 * @param {AddCostUnitNodeRequest} nodes
 * @param {number} xversion
 * @returns {BluebirdPromise<any>}
 */
export function saveCostUnitNodes(companyId, nodes, xversion) {
    let requestBuilder = new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes");
    if (xversion != null) {
        requestBuilder.xVersion(xversion);
    }
    return requestBuilder
        .jsonData(nodes)
        .post();
}

export function getCostUnitsCombinations(companyId, nodeid, level, parentid = null) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path(nodeid)
        .path("children")
        .get();
}

export function saveCostUnitsCombination(companyId, nodeId, dimension, costUitNumber) {
    const data = {
        dimension: dimension,
        costUnitNumber: costUitNumber,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path(nodeId)
        .path("children")
        .jsonData(data)
        .post();
}

export function exportCostUnits(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path("export")
        .queryParam("obfuscate", true)
        .get();
}

export function getBranches(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("branches")
        .get();
}

export function getExceptions() {
    return call.get(getApiRootUrl() + "/RoleNameExceptions");
}

export function getExceptionsExtended(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("role-name-exceptions")
        .path("exceptions")
        .get();
}

export function getRoleNameExceptions(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("role-name-exceptions")
        .get();
}

export function searchExceptionsRole(companyId, text) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("roles")
        .queryParam("name", text)
        .get();
}

export function getExceptionsRole(role) {
    return call.get(getApiRootUrl() + `/Roles/${role}`);
}

export function saveExceptions(companyId, exceptions) {
    let data = {
        roleNameExceptionDTOList: exceptions,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("role-name-exceptions")
        .jsonData(data)
        .put();
}

export function saveRoleNameException(companyId, data) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("role-name-exceptions")
        .jsonData(data)
        .post();
}

export function getRoleNameException(companyId, id) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("role-name-exceptions")
        .path(id)
        .get();
}

export function updateRoleNameException(companyId, id, data) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("role-name-exceptions")
        .path(id)
        .jsonData(data)
        .put();
}

export function deleteRoleNameException(companyId, id) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("role-name-exceptions")
        .path(id)
        .delete();
}

export function getCostUnitWarnings(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path("dimension-warnings")
        .get();
}

export function getDeletedCostUnitWarnings(companyId, data) {
    const {page, rows} = data;
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path("warnings")
        .queryParam("page", page)
        .queryParam("rows", rows)
        .get();
}


export function deleteCostUnitNode(companyId, id, confirm, version) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path(id)
        .queryParam("confirm", confirm)
        .header(IF_MATCH_HEADER, version)
        .delete();
}

export function getNodeAncestors(companyId, id) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path(id)
        .path("ancestors")
        .get();
}

//endregion

//region New endpoints for assign Cost Unit Role

export function getCostUnitRoles(companyId, nodeId, level, sortColumn = "approver", sortOrder = "asc") {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path(nodeId)
        .path("assignments")
        .queryParam("approver", "")
        .queryParam("role", "all")
        .queryParam("costCenterType", "all")
        .queryParam("costUnitName", "")
        .queryParam("page", 1)
        .queryParam("rows", 50)
        .queryParam("sortColumn", sortColumn)
        .queryParam("sortOrder", sortOrder)
        .queryParam("level", level)
        .get();
}

export function saveNewCostUnitRole(companyId, nodeId, roleId, userId, branchId, deputy, skip) {
    let data = {
        roleId: roleId,
        userId: userId,
        skip: skip,
        deputy: deputy,
        branchId: branchId === 0 ? null : branchId,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path(nodeId)
        .path("assignments")
        .jsonData(data)
        .post();
}

export function editCostUnitRole(companyId, nodeId, roleId, userId, branchId, deputy, skip, oldRoleId, oldUserId, oldBranchId) {
    let data = {
        roleId: roleId,
        userId: userId,
        skip: skip,
        deputy: deputy,
        branchId: branchId === 0 ? null : branchId,
    };
    const requestBuilder = new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path(nodeId)
        .path("assignments")
        .path(oldUserId)
        .path(oldRoleId);
    if (oldBranchId) {
        requestBuilder.path(oldBranchId);
    }
    return requestBuilder.jsonData(data).put();
}

export function deleteCostUnitRole(companyId, nodeId, roleId, userId, branchId) {
    const requestBuilder = new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path(nodeId)
        .path("assignments")
        .path(userId)
        .path(roleId);
    if (branchId) {
        requestBuilder.path(branchId);
    }
    return requestBuilder.delete();
}

export function editSkippedCostUnitRole(companyId, nodeId, roleId, userId, branchId, deputy, skip, oldRoleId, oldBranchId) {
    let data = {
        roleId: roleId,
        userId: userId,
        skip: skip,
        deputy: deputy,
        branchId: branchId === 0 ? null : branchId,
    };
    const requestBuilder = new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path(nodeId)
        .path("assignments")
        .path("skip")
        .path(oldRoleId);
    if (oldBranchId) {
        requestBuilder.path(oldBranchId);
    }
    return requestBuilder.jsonData(data).put();
}

export function deleteSkippedCostUnitRole(companyId, nodeId, roleId, branchId) {
    const requestBuilder = new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("cost-unit-nodes")
        .path(nodeId)
        .path("assignments")
        .path("skip")
        .path(roleId);
    if (branchId) {
        requestBuilder.path(branchId);
    }
    return requestBuilder.delete();
}

//endregion

//region RuleConfiguration

export function activateRule(companyId, rule, state) {
    rule.active = state;
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .path(rule.idRuleConfiguration)
        .jsonData(rule)
        .put();
}

export function activateReadonlyRule(companyId, ruleConfigurationId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .path(ruleConfigurationId)
        .path("activate")
        .post();
}

export function deactivateReadonlyRule(companyId, ruleConfigurationId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .path(ruleConfigurationId)
        .path("deactivate")
        .post();
}

export function addRule(companyId, name, docType) {
    let data = {
        name: name,
        documenttype:
            {id: docType},
        steps: [],
        active: false,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .jsonData(data)
        .post();
}

export function copyRule(companyId, ruleId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .path(ruleId)
        .path("copy")
        .post();
}

export function copyRuleWithName(companyId, rule) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .jsonData(rule)
        .post();
}

export function deleteRule(companyId, ruleId, comment) {
    let requestBuilder = new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .path(ruleId);
    if (comment) {
        requestBuilder.header("deleteReasonBase64", encodeToBase64UTF8(comment));
    }
    return requestBuilder.delete();
}

export function getCopiedRuleConfiguration(companyId, ruleId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .path(ruleId)
        .get();
}

export function getRuleConfiguration(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .get();
}

export function saveRule(companyId, rule, comment) {
    let requestBuilder = new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .path(rule.idRuleConfiguration);
    if (comment) {
        requestBuilder.header("updateReasonBase64", encodeToBase64UTF8(comment));
    }
    return requestBuilder
        .jsonData(rule)
        .put();
}

export function _simulateWorkflowGraph(documentKey, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("documents")
        .path(documentKey)
        .path("simulate")
        .post();
}

export function getSimulatorProjects() {
    return call.get(getApiRootUrl() + "/ProjectManagerBean/Projects");
}

export function getSimulatorDepartments() {
    return call.get(getApiRootUrl() + "/DepartmentManagerBean/Departments");
}

export function findEmployees(prefix, companyId) {
    let data = {
        prefix: prefix,
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("employees")
        .path("search")
        .formData(data)
        .post();
}

export function simulateWorkflowGraph(companyId, workflow) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .jsonData(workflow)
        .path("simulate")
        .post();

}

export function validateWorkflowGraph(companyId, workflow) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .jsonData(workflow)
        .path("validate")
        .post();
}

//endregion

//region SubstituteBean

export function getSubstitutesSortedOnPage(page, typeFilter, prefix, sort) {
    const data = {
        prefix: prefix,
        interval: typeFilter,
        fields: JSON.stringify([sort]),
        page: page,
        itemsPerPage: 13,
    };

    return call.get(getApiRootUrl() + "/substitute" + call.objectToQueryString(data));
}

export function saveSubstitute(data) {
    return call.postJson(getApiRootUrl() + "/substitute", data);
}

export function getSubstitute(id) {
    return call.get(getApiRootUrl() + "/substitute/" + id);
}

export function validateSubstitute(data) {
    return call.postJson(getApiRootUrl() + "/substitute/validate", data);
}

export function getCompaniesForSubstitutes(companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("substitutes")
        .path("companies")
        .get();
}


export function getSubstitutesForUser(userId, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("substitutes")
        .path(userId)
        .get();

}

//endregion

//region SystemOverview / ProcessOverview

export function getProcessOverview(data, type) {
    return call.postForm(getApiRootUrl() + "/SystemOverviewBean/" + type, data);
}

export function getProcessOverviewSse(filters, conf, cursor) {
    let headers = {
        "X-Requested-With": "UI",
    };
    // This is the only way to use a constant for the value of the key
    headers[CONTEXT_ID_HEADER] = window.currentContext;
    if (cursor != null) {
        headers["last-event-id"] = cursor;
    }

    let url = "/SystemOverviewBean/stream?limit=40";
    if (filters.columns != null) {
        for (const [column, value] of Object.entries(filters.columns)) {
            const trimmedValue = value.trim(); //trim the value
            url = url.concat("&" + encodeURIComponent(column) + "=", encodeURIComponent(trimmedValue));
        }
    }
    if (filters.costUnits != null && filters.costUnits.length > 0) {
        url = url.concat("&costUnits=", encodeURIComponent(JSON.stringify(filters.costUnits)));
    }
    if (filters.account != null) {
        url = url.concat("&account=", encodeURIComponent(JSON.stringify(filters.account)));
    }
    return fetchEventSource(getApiRootUrl() + url, {
        signal: conf.signal,
        headers: headers,
        credentials: "include",
        openWhenHidden: true,
        onopen: conf.onopen,
        onmessage: conf.onmessage,
        onclose: conf.onclose,
        onerror: conf.onerror,
    });
}

export function getAllProcessOverviewStates() {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("reference-data")
        .path("process-states")
        .path("all")
        .get();
}


//endregion

//region TaskBean

export function postponeTask(taskKeys, comment, formattedNewDueDate, daysAfterTaskDueDate, daysBeforeDocumentDueDate) {

    const data = {
        taskKeys: taskKeys,
        /**
         * Format: yyyy-MM-dd
         */
        formattedNewDueDate: formattedNewDueDate,
        comment: comment,
        daysAfterTaskDueDate: daysAfterTaskDueDate,
        daysBeforeDocumentDueDate: daysBeforeDocumentDueDate,
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path("postpone")
        .jsonData(data)
        .post();
}


export function getDocumentEditor(taskKey) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(taskKey)
        .path("external-editor")
        .get();
}

export function updateTask(taskKey, version, skip) {
    let data = {
        documentVersion: version,
        skipWorkflowRestart: skip,
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(taskKey)
        .path("update-document")
        .jsonData(data)
        .post();
}


export function updateDocument(documentKey, version, skip, companyId) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("documents")
        .path(documentKey)
        .path("update-document")
        .post();
}

export function getTaskDocumentDetails(taskKey) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(taskKey)
        .path("document-details")
        .get();
}

export function postTaskDocumentDetails(taskKey, data) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(taskKey)
        .path("document-details")
        .jsonData(data)
        .post();
}

export function getAllTasks() {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("my-tasks")
        .get();
}

export function getListOfTasks(taskIds) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("my-tasks")
        .queryParam("taskIds", taskIds)
        .get();
}

export function getTaskDetails(taskKey) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(taskKey)
        .path("details")
        .get();
}

export function getTaskDetailsByUID(uid) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(uid)
        .get();
}

export function getDeletedTaskByUID(uid) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("deleted-tasks")
        .path(uid)
        .get();
}

//endregion

//region Task Overview

export function getTaskOverviewCounters() {
    return call.get(getApiRootUrl() + "/TaskOverviewBean/getAllTaskOverviewCounters");
}

export function getAssignmentType() {
    return call.get(getApiRootUrl() + "/TaskOverviewBean/getAssignmentType");
}

export function getTaskOverview(data) {
    return call.postForm(getApiRootUrl() + "/TaskOverviewBean/getTaskOverview", data);
}

export function OverviewReassign(taskKeys, comment, recipient) {

    const data = {
        selectedTasks: taskKeys,
        comment: comment,
        recipient: recipient,
    };
    return call.postJson(getApiRootUrl() + "/TaskOverviewBean/reassign", data);
}


//endregion

//region WorkflowActions

export function approveTask(taskKey, comment = "", docVersion, skipWorkflowRestart) {
    let data = {};
    if (comment)
        data.comment = comment;
    if (docVersion)
        data.documentVersion = docVersion;
    if (skipWorkflowRestart)
        data.skipWorkflowRestart = skipWorkflowRestart;

    data.approveAllLines = true;

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(taskKey)
        .path("approve")
        .jsonData(data)
        .post();
}

export function forwardTask(taskKey, userComment, recipientId) {
    const data = {
        comment: userComment,
        recipient: recipientId,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(taskKey)
        .path("forward")
        .jsonData(data)
        .post();
}

export function rejectTask(taskKey, comment) {
    const data = {
        comment: comment || "",
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(taskKey)
        .path("reject")
        .jsonData(data)
        .post();
}

export function rejectTaskByUID(uid, comment) {
    const data = {
        comment: comment || "",
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(uid)
        .path("reject")
        .jsonData(data)
        .post();
}

export function reviewTask(taskKey, comment) {
    const data = {
        comment: comment || "",
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(taskKey)
        .path("complete-review")
        .jsonData(data)
        .post();
}

export function requestReviewTask(taskKey, recipientId, comment) {
    const data = {
        comment: comment,
        recipient: recipientId,
    };

    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tasks")
        .path(taskKey)
        .path("review")
        .jsonData(data)
        .post();
}

//endregion


//region WorkflowHistory
export function getWorkflowHistory(companyId, data) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations-history")
        .queryParams(data)
        .get();
}

export function getRuleConfigurationHistory(companyId, ruleId) {
    let data = {
        page: 1,
        rows: 20,
        ruleConfigId: ruleId,
    };
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations-history")
        .queryParams(data)
        .get();
}

export function getOldWorkflowJSON(companyId, ruleId, version) {
    return new RequestBuilder(getApiRootUrl(), IGNORE_VISMA_CONTEXTID)
        .path("tenant")
        .path(companyId)
        .path("rule-configurations")
        .path(ruleId)
        .path("versions")
        .path(version)
        .get();
}

/**
 * Expected return structure: {"id": "string","url": "string"}
 * @param relativeUrl
 * @returns Promise<{{id: string, url: string}}>
 */
export function getArchiveCallbackUrl(relativeUrl) {
    return new RequestBuilder(getApiRootUrl() + relativeUrl, IGNORE_VISMA_CONTEXTID)
        .get();
}

//endregion
export function cancelRequests(requests) {
    return requests.forEach(p => p.cancel());
}
